import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid2";
import {
  Typography,
  TextField,
  Autocomplete,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Avatar,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Stack } from "@mui/system";
import dayjs from "dayjs";
import {
  selectAllUsers,
  selectUserDetails,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { ActionItemsEndPointLogic } from "../../../../../../logic/AssetManagement/ActionItems/actionItemsEndPointLogic";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  isEmpty,
  validateActionItemName,
  validateDateFormat,
  validateText,
} from "../../../../../../utils/actionItemsValidation";
import FileUploadActionItem from "./FileUploadActionItem";
import { useActionItemFileUpload } from "../../../../../../customHooks/useActionItemFileUpload";
import { PriorityOptions } from "../../../../../../constants/PriorityOption";

interface SingleItemCreateProps {
  open: boolean;
  handleClose: () => void;
}

export default function SingleItemCreate({
  open,
  handleClose,
}: SingleItemCreateProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const allUsers = useSelector(selectAllUsers);
  const owner = useSelector(selectUserDetails) as any;
  const task_structure_shared_uid = "484362c5f00c4866ad1b4562b47aa27b";
  let currentPath = window.location.origin + location.pathname;
  const segment_type = "website";
  const selectedWebsiteSharedUid = useSelector(
    (state: any) => state?.actionItems?.selectedSearchSite
  );

  const {
    uploadedFiles,
    handleFileUpload,
    setUploadedFiles,
    handleRemoveFile,
    handleFileDrop,
    isDragging,
    setIsDragging,
    uploadFilesActionItem,
  } = useActionItemFileUpload(selectedWebsiteSharedUid, "create_page");

  const initialData = {
    action_item: "",
    category: "",
    target: "",
    description: "",
    priority: "",
    due_date: "",
    assignee: "",
  };

  const [actionItemData, setActionItemData] = useState<any>(initialData);
  const [formError, setFormError] = useState<any>(initialData);

  const handleActionItemChange = (name: string, value: any) => {
    setActionItemData({
      ...actionItemData,
      [name]: value,
    });

    let errorMsg = "";
    if (name === "action_item") {
      errorMsg = validateActionItemName(value);
    } else if (name === "category") {
      errorMsg = validateText(value);
    } else if (name === "target") {
      errorMsg = validateText(value);
    } else if (name === "due_date") {
      errorMsg = validateDateFormat(value);
    } else if (name === "assignee") {
      errorMsg = isEmpty(value);
    } else if (name === "priority") {
      errorMsg = isEmpty(value);
    }

    setFormError((prevErrors: any) => ({
      ...prevErrors,
      [name]: errorMsg,
    }));
  };

  // -- Set owner, task_structure_shared_uid and reference id for action item
  useEffect(() => {
    if (owner?.user_id !== "" && selectedWebsiteSharedUid) {
      setActionItemData((prevstate: any) => ({
        ...prevstate,
        owner: owner.user_id,
        reference_id: selectedWebsiteSharedUid,
        task_structure_shared_uid: task_structure_shared_uid,
        reference_url: `${currentPath}/edit_action_item?selectedSite=${selectedWebsiteSharedUid}`,
      }));
    }
    // eslint-disable-next-line
  }, [open, owner?.user_id, selectedWebsiteSharedUid]);

  //-- Get Users
  useEffect(() => {
    ActionItemsEndPointLogic.getAllUserList(oauthTokenHeader);
  }, [oauthTokenHeader]);

  // Reset data when modal closes
  useEffect(() => {
    if (!open) {
      setActionItemData(initialData);
    }
    // eslint-disable-next-line
  }, [open]);

  //-- Close the modal and reset the modal data
  const handleCloseSingleItem = () => {
    handleClose(); // close the Modal
    setUploadedFiles([]); // reset uploaded files
    setFormError(
      Object.fromEntries(Object.keys(initialData).map((key) => [key, ""]))
    ); // reset form errors
    navigate(`/tools/action_items${location.search}`); // Redirect to the Action Items List
  };

  //-- Clicking on create new action item button
  const handleCreateActionItem = async () => {
    // -- Request body for Make API call
    const createRequestBodyActionItems = {
      tasks: [
        {
          task_structure_shared_uid: actionItemData?.task_structure_shared_uid,
          owner: actionItemData?.owner,
          assignee: actionItemData?.assignee,
          reference_id: actionItemData?.reference_id,
          due_date: actionItemData?.due_date,
          segment_type: segment_type,
          segment_value: actionItemData?.reference_id,
          website_shared_uid: actionItemData?.reference_id,
          reference_url: actionItemData?.reference_url,
          data: {
            category: actionItemData?.category,
            target: actionItemData?.target,
            description: actionItemData?.description,
            priority: actionItemData?.priority,
            action_item: actionItemData?.action_item,
          },
        },
      ],
    };

    try {
      setLoading(true);
      const res = await ActionItemsEndPointLogic.CreateNewActionItem(
        oauthTokenHeader,
        createRequestBodyActionItems
      );

      if (res?.reason !== "success") {
        throw new Error(`Failed to Create new task.`);
      }
      //-- This for File upload if we select any file
      if (uploadedFiles?.length > 0) {
        await uploadFilesActionItem(res?.data?.[0]?.data?.shared_uid);
      }

      await ActionItemsEndPointLogic.getAllActionItems(
        selectedWebsiteSharedUid,
        oauthTokenHeader,
        dispatch
      );
      handleCloseSingleItem();
      dispatch(
        setDisplayErrorSnackBar({
          message: "Action item has been created successfully.",
          type: "success",
        })
      );
    } catch (error) {
      // Ensure error message is a string
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
          ? error.message
          : "An unexpected error occurred.";

      dispatch(
        setDisplayErrorSnackBar({
          message: `Failed to Create new task. ${errorMessage}`,
          type: "error",
        })
      );

      return; // Stop execution, do not close modal
    } finally {
      setLoading(false);
    }
  };

  //-- Check if form is valid (all fields must pass validation)
  const isFormValid =
    Object.values(formError).every((err) => !err) && // Ensure no errors exist
    Object.entries(actionItemData) // Convert object to array of key-value pairs
      .every(
        ([_, value]) =>
          value !== null &&
          value !== undefined &&
          value.toString().trim() !== ""
      );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (
          loading &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }
        handleCloseSingleItem();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ padding: "16px 24px 0px 24px" }}>
        {"Add New Items"}
      </DialogTitle>
      <Typography sx={{ padding: "16px 24px 0px 24px" }}>Details</Typography>
      <DialogContent
        sx={{ padding: "16px 24px", paddingTop: "16px !Important" }}
      >
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Action Item Name"
              size="small"
              color="secondary"
              placeholder="Name this task"
              required
              value={actionItemData?.action_item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleActionItemChange("action_item", e.target.value)
              }
              error={!!formError?.action_item}
              helperText={formError?.action_item}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Category "
              size="small"
              color="secondary"
              placeholder="This item relates to"
              required
              value={actionItemData?.category}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleActionItemChange("category", e.target.value)
              }
              error={!!formError?.category}
              helperText={formError?.category}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Target"
              size="small"
              color="secondary"
              placeholder="Specify your target for this item"
              required
              value={actionItemData?.target}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleActionItemChange("target", e.target.value)
              }
              error={!!formError?.target}
              helperText={formError?.target}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
          <Grid size={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Due Date"
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    error: !!formError?.due_date,
                    helperText: formError?.due_date || "",
                    size: "small",
                    color: "secondary",
                    InputLabelProps: { shrink: true },
                    placeholder: "Pick a date",
                    inputProps: {
                      readOnly: true,
                      onKeyDown: (e) => {
                        // Block ALL keys including numbers and delete
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onKeyPress: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onKeyUp: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onInput: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onBeforeInput: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onPaste: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onCut: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onChange: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      },
                      onFocus: (e) => {
                        e.target.blur(); // Remove focus immediately
                      },
                      style: {
                        pointerEvents: "none",
                        caretColor: "transparent",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        MozUserSelect: "none",
                        cursor: "default",
                      },
                    },
                    sx: {
                      "& .MuiInputBase-input": {
                        pointerEvents: "none !important",
                        caretColor: "transparent !important",
                        userSelect: "none !important",
                        WebkitUserSelect: "none !important",
                        MozUserSelect: "none !important",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                        },
                      },
                      "& .MuiIconButton-root": {
                        pointerEvents: "auto !important",
                        cursor: "pointer !important",
                      },
                    },
                  },
                }}
                format="DD/MM/YYYY"
                minDate={dayjs()} // Prevent selection of past dates
                disablePast // Disables all past dates automatically
                onChange={(date) => {
                  if (date)
                    handleActionItemChange(
                      "due_date",
                      dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                    );
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <Grid size={6}>
            <Autocomplete
              disablePortal={false}
              options={allUsers}
              getOptionLabel={(option) =>
                `${option?.first_name || ""} ${option?.last_name || ""}`
              } // Display name correctly
              value={
                allUsers?.find(
                  (user: any) => user?.id === actionItemData?.assignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleActionItemChange("assignee", newValue?.id || null)
              }
              fullWidth
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.id}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <Avatar
                    src={option?.avatarUrl} // Ensure this field exists in your user data
                    sx={{ width: 24, height: 24, fontSize: 12 }}
                  >
                    {option?.first_name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  {option?.first_name} {option?.last_name}
                </li>
              )}
              renderInput={(params) => {
                const selectedUser = params.inputProps.value
                  ? allUsers.find(
                      (user: any) =>
                        `${user?.first_name} ${user?.last_name}` ===
                        params.inputProps.value
                    )
                  : null;

                return (
                  <TextField
                    {...params}
                    label="Assignee"
                    size="small"
                    required
                    aria-readonly
                    error={!!formError?.assignee}
                    helperText={formError?.assignee}
                    color="secondary"
                    placeholder="Choose Stakeholder from the list"
                    sx={{ input: { cursor: "pointer" } }} // show pointer on hover
                    onKeyDown={(e) => e.preventDefault()} // Blocks manual typing
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedUser ? (
                        <Avatar
                          src={selectedUser.avatarUrl}
                          sx={{ width: 24, height: 24, marginRight: 1 }}
                        >
                          {selectedUser.first_name.charAt(0).toUpperCase()}
                        </Avatar>
                      ) : null,
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid size={6}>
            <Autocomplete
              options={PriorityOptions}
              fullWidth
              getOptionLabel={(option) => option?.label}
              value={
                PriorityOptions.find(
                  (opt) => opt.label === actionItemData?.priority
                ) || null
              }
              onChange={(_, newValue: any) =>
                handleActionItemChange("priority", newValue?.label || null)
              }
              renderOption={(props, option) => (
                <ListItem {...props} key={option.label}>
                  <ListItemIcon>{option.icon}</ListItemIcon>
                  <ListItemText primary={option.label} />
                </ListItem>
              )}
              renderInput={(params) => {
                // Find the selected option to show the correct icon
                const selectedOption = PriorityOptions.find(
                  (opt) => opt.label === params.inputProps.value
                );
                return (
                  <TextField
                    {...params}
                    label="Priority"
                    size="small"
                    color="secondary"
                    required
                    error={!!formError?.priority}
                    helperText={formError?.priority}
                    placeholder="Choose from the list."
                    inputProps={{
                      ...params.inputProps,
                      sx: { cursor: "pointer" },
                    }} // Ensures dropdown opens
                    onKeyDown={(e) => e.preventDefault()} // Blocks manual typing
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedOption ? (
                        <InputAdornment position="start">
                          {selectedOption.icon}
                        </InputAdornment>
                      ) : null,
                    }}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <Grid size={12}>
            <TextField
              color="secondary"
              fullWidth
              id="outlined-multiline-static"
              label="Description"
              multiline
              rows={4}
              value={actionItemData?.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const inputText = e.target.value;
                if (inputText.length > 500) {
                  handleActionItemChange(
                    "description",
                    inputText.slice(0, 500)
                  ); // Trims the excess characters
                } else {
                  handleActionItemChange("description", inputText);
                }
              }}
              error={!!formError?.description}
              helperText={
                formError?.description
                  ? formError?.description
                  : `Max 500 characters allowed.`
              }
              placeholder="Add your Statement here"
              required
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
        </Grid>

        {/*---- File Upload -----*/}
        <FileUploadActionItem
          isDragging={isDragging}
          setIsDragging={setIsDragging}
          handleFileDrop={handleFileDrop}
          handleFileUpload={handleFileUpload}
          uploadedFiles={uploadedFiles}
          handleRemoveFile={handleRemoveFile}
        />
      </DialogContent>
      <DialogActions sx={{ padding: "0px 24px 24px 24px" }}>
        <Button
          color="secondary"
          onClick={handleCloseSingleItem}
          disabled={loading}
        >
          CANCEL
        </Button>
        <Button
          data-tracker="122"
          color="secondary"
          variant="contained"
          loading={loading}
          loadingPosition="end"
          endIcon={loading && <SaveIcon />}
          disabled={!isFormValid}
          onClick={handleCreateActionItem}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            ADD Action Item <CheckIcon fontSize="small" />
          </Stack>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
